'use client'

import Link from 'next/link'

import { Icon } from '@/components/ui/icon'
import type { IconName } from '@/config/icons'

interface ServiceCard {
  icon: IconName
  name: string
  description: string
  href: string
}

// These tiles link into the real service catalog (/services/<slug>). Window tint is
// deliberately absent: it is no longer a service the shop sells.
const SERVICES: ServiceCard[] = [
  {
    icon: 'cw-colour-change',
    name: 'Colour Change Wrap',
    description: 'Change your colour without touching the paint. Full and partial coverage, in the finish you want.',
    href: '/services/color-change-wrap',
  },
  {
    icon: 'cw-paint-protection',
    name: 'Paint Protection Film',
    description: 'Clear film that takes the rock chips and scratches so your paint does not.',
    href: '/services/paint-protection-film',
  },
  {
    icon: 'cw-fleet-delivery',
    name: 'Commercial Graphics',
    description: 'Printed graphics that turn your vehicle into a moving billboard. Built for fleets.',
    href: '/services/commercial-graphics',
  },
  {
    icon: 'cw-decal',
    name: 'Commercial Decals',
    description: 'Logo, name, phone, and website in clean decals. The cheapest way to brand a vehicle.',
    href: '/services/commercial-decal-package',
  },
]

export function ServicesSection() {
  return (
    <section className="cw-section">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="mb-12 flex flex-col gap-3">
          <p className="cw-eyebrow">what we do</p>
          <h2 className="cw-display-2 text-foreground">Our Services</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((service) => (
            <Link
              key={service.name}
              href={service.href}
              className="group relative flex min-h-[280px] flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-surface p-7 transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]"
            >
              {/* Oversized teal glyph bleeding off the bottom-right corner, at
                  12% so it reads as a watermark behind the copy rather than an
                  icon (brand-recolour handoff, homepage "Our services"). The
                  Icon component sizes by font-size, so 200px is a text- class,
                  not w-/h-. */}
              <Icon
                icon={service.icon}
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-14 -right-11 z-0 text-[200px] leading-none text-[color:var(--cw-lime)] opacity-[.12]"
              />
              <h3 className="cw-display-3 relative z-10 text-foreground">{service.name}</h3>
              <p className="relative z-10 flex-1 text-sm leading-relaxed text-muted">
                {service.description}
              </p>
              <span className="relative z-10 flex items-center gap-2 text-sm font-semibold text-[color:var(--cw-teal-btn)]">
                View service
                <Icon
                  icon="cw-arrow-right"
                  className="text-sm transition-transform group-hover:translate-x-1"
                />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
